import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Headphones, AlertCircle, QrCode } from 'lucide-react';
import { isValidRoomId } from '@speech-translator/shared';

export function JoinRoomPage() {
  const [roomId, setRoomId] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if room ID was provided via URL params
  const urlRoomId = searchParams.get('room');
  if (urlRoomId && !roomId) {
    setRoomId(urlRoomId);
  }

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleJoinRoom = () => {
    const trimmedRoomId = roomId.trim();
    
    if (!trimmedRoomId) {
      setError('Please enter a room ID');
      return;
    }

    if (!isValidRoomId(trimmedRoomId)) {
      setError('Invalid room ID format. Room ID should be 6-32 characters long and contain only letters, numbers, hyphens, and underscores.');
      return;
    }

    setError('');
    navigate(`/listener/${trimmedRoomId}`);
  };

  const handleInputChange = (value: string) => {
    setRoomId(value);
    if (error) {
      setError('');
    }
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-md mx-auto">
        <Button 
          variant="outline" 
          onClick={handleBackToHome}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        <Card>
          <CardHeader className="text-center">
            <div className="p-3 bg-secondary/10 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Headphones className="h-8 w-8 text-secondary-foreground" />
            </div>
            <CardTitle>Join Room</CardTitle>
            <CardDescription>
              Enter the room ID to start receiving live translations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Room ID Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Room ID</label>
              <input
                type="text"
                placeholder="Enter room ID (e.g., abc123def)"
                className={`w-full px-3 py-2 border rounded-md transition-colors ${
                  error 
                    ? 'border-destructive focus:border-destructive' 
                    : 'border-input focus:border-primary'
                } focus:outline-none focus:ring-2 focus:ring-primary/20`}
                value={roomId}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleJoinRoom();
                  }
                }}
                autoFocus
              />
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>

            {/* Join Button */}
            <Button 
              className="w-full" 
              onClick={handleJoinRoom}
              disabled={!roomId.trim()}
            >
              <Headphones className="h-4 w-4 mr-2" />
              Join Room
            </Button>

            {/* Alternative Methods */}
            <div className="space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or
                  </span>
                </div>
              </div>

              <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <QrCode className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Scan QR code from speaker
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Ask the speaker to show their QR code and scan it with your camera
                </p>
              </div>
            </div>

            {/* Help */}
            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <h4 className="text-sm font-medium">Need help?</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Room IDs are provided by speakers</li>
                <li>• Check that the room ID is correct</li>
                <li>• Make sure the speaker has started their room</li>
              </ul>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-2 justify-center">
              <Badge variant="secondary" className="text-xs">
                Real-time translation
              </Badge>
              <Badge variant="secondary" className="text-xs">
                10+ languages
              </Badge>
              <Badge variant="secondary" className="text-xs">
                No registration
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}